"use client";

import { useActionState, useEffect, useRef } from "react";
import { useFormStatus } from "react-dom";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Loader2Icon } from "lucide-react";
import { loginFormAction, type LoginState } from "./actions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

function SubmitButton({
  pendingLabel,
  children,
  ...props
}: React.ComponentProps<typeof Button> & { pendingLabel: string }) {
  const { pending, data } = useFormStatus();
  const submittedIntent = data?.get("intent");
  const isActive =
    pending &&
    (props.value == null ||
      submittedIntent == null ||
      submittedIntent === props.value);

  return (
    <Button type="submit" {...props} disabled={pending}>
      {isActive ? (
        <>
          <Loader2Icon className="size-4 animate-spin" />
          {pendingLabel}
        </>
      ) : (
        children
      )}
    </Button>
  );
}

function FormBusy({ children }: { children: React.ReactNode }) {
  const { pending } = useFormStatus();
  return (
    <fieldset
      disabled={pending}
      aria-busy={pending}
      className="min-w-0 space-y-4 border-0 p-0 disabled:opacity-80"
    >
      {children}
    </fieldset>
  );
}

export function LoginForm({ initialState }: { initialState: LoginState }) {
  const router = useRouter();
  const otpRef = useRef<HTMLInputElement>(null);
  const [state, formAction] = useActionState(loginFormAction, initialState);
  const onCodeStep = state?.step === "code";
  const error = state && "error" in state ? state.error : undefined;
  const message = state && "message" in state ? state.message : undefined;

  useEffect(() => {
    if (onCodeStep && otpRef.current) otpRef.current.value = "";
  }, [onCodeStep, message]);

  useEffect(() => {
    const url = new URL(window.location.href);
    let dirty = false;
    for (const key of ["password", "email", "resent"] as const) {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
        dirty = true;
      }
    }
    if (!dirty) return;
    const qs = url.searchParams.toString();
    router.replace(qs ? `${url.pathname}?${qs}` : url.pathname);
  }, [router]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-100 px-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Admin Login</CardTitle>
          <CardDescription>
            {onCodeStep
              ? "Enter the verification code we emailed you"
              : "XL Law RSVP administration"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {onCodeStep ? (
            <form action={formAction}>
              <FormBusy>
                {message && (
                  <p className="text-sm text-neutral-600">{message}</p>
                )}
                <div className="space-y-2">
                  <Label htmlFor="otp">Verification code</Label>
                  <Input
                    ref={otpRef}
                    id="otp"
                    name="code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    maxLength={6}
                    placeholder="000000"
                    required
                    autoFocus
                    pattern="\d{6}"
                    onInput={(e) => {
                      const el = e.currentTarget;
                      el.value = el.value.replace(/\D/g, "").slice(0, 6);
                    }}
                    className="h-12 text-center font-mono text-2xl tracking-[0.4em] text-neutral-900 placeholder:tracking-[0.4em] placeholder:text-neutral-300"
                  />
                </div>
                {error && <p className="text-sm text-red-600">{error}</p>}
                <SubmitButton
                  name="intent"
                  value="verify"
                  className="w-full"
                  pendingLabel="Verifying..."
                >
                  Verify and sign in
                </SubmitButton>
                <div className="flex gap-2">
                  <SubmitButton
                    name="intent"
                    value="resend"
                    formNoValidate
                    variant="outline"
                    className="flex-1"
                    pendingLabel="Sending..."
                  >
                    Resend code
                  </SubmitButton>
                  <SubmitButton
                    name="intent"
                    value="cancel"
                    formNoValidate
                    variant="ghost"
                    className="flex-1"
                    pendingLabel="..."
                  >
                    Back
                  </SubmitButton>
                </div>
              </FormBusy>
            </form>
          ) : (
            <form action={formAction}>
              <FormBusy>
                <input type="hidden" name="intent" value="login" />
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    autoFocus
                    autoComplete="username"
                    defaultValue=""
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    required
                    autoComplete="current-password"
                    defaultValue=""
                  />
                </div>
                {error && <p className="text-sm text-red-600">{error}</p>}
                <SubmitButton className="w-full" pendingLabel="Sending code...">
                  Continue
                </SubmitButton>
              </FormBusy>
            </form>
          )}
          <p className="mt-4 text-center text-sm text-neutral-500">
            <Link href="/" className="hover:underline">
              Back to site
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
